import {
  DialogContentActive, DialogContentButton,
  DialogContentSelected,
  DialogContentSelectedBin,
  DialogContentSelectedSubTitle,
  DialogContentSelectedTitle, DialogContentText,
  DialogUploadCloseButton, FileIconBox,
  UploadColorText,
  UploadSwitch,
  UploadText,
} from "./uploadModal.styled";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide, Typography,
} from "@mui/material";
import {CloseModalIcon} from "../../assets/svg/closeModalIcon";
import React, {forwardRef, PropsWithChildren, useEffect, useImperativeHandle, useState} from "react";
import {TransitionProps} from "@mui/material/transitions";
import {SwitchIcon} from "../../assets/svg/switch/switchIcon";
import {SwitchUncheckedIcon} from "../../assets/svg/switch/switchUncheckedIcon";
import {useDropzone} from "react-dropzone";
import Api from "../api/api";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {encryptFile} from "../../utils/fileAction";
import {FileIcon} from "../../assets/svg/fileIcon";
import {compactFileName, formatFileSize} from "../../page/dashboard/includes";
import {BinIcon} from "../../assets/svg/binIcon";
import {UiAddButton} from "../ui/addButton";
import {Field, Formik} from "formik";
import {UploadModalSchema} from "./model/uploadModal.schema";
import {UiGradientInput} from "../ui/includes/GradientInput";

export interface UploadModalRef {
  openModal: () => void;
  closeModal: () => void;
}

const Transition = forwardRef(function Transition(
  props: PropsWithChildren<TransitionProps> & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const api = Api();

export const UploadModal = forwardRef<UploadModalRef>((_, ref) => {
  const [openModal, setOpenModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [secretPhrase, setSecretPhrase] = useState<string>('');
  const [checked, setChecked] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useImperativeHandle(ref, () => ({
    openModal: () => setOpenModal(true),
    closeModal: () => setOpenModal(false)
  }));

  const queryClient = useQueryClient();

  const {mutateAsync} = useMutation({
    mutationKey: ['encodeFile', file, secretPhrase],
    mutationFn: async () => {
      try {
        if (!file) {
          throw new Error('Please select a file');
        }

        if (!checked) {
          await api.updloadFile(file);
        } else {
          if (!secretPhrase) {
            throw new Error('Please enter a secret phrase');
          }

          await encryptFile(file, secretPhrase);
        }

        queryClient.invalidateQueries({queryKey: ['getFiles']});
        setOpenModal(false)
      } catch (error: any) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      setFile(null)
      setSecretPhrase('')
    },
    onError: (error) => {
      if (error) {
        setErrorMessage('Incorrect encryption key')
      }
    },
  });

  useEffect(() => {
    if (openModal) {
      setFile(null)
    }
  }, [openModal]);

  useEffect(() => {
    if (checked) {
      setSecretPhrase('')
    }
  }, [checked]);

  const onDrop = (newFile: File[]) => {
    setFile(newFile[0])
  }

  const {getRootProps, getInputProps, open, isDragActive} = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
  })

  const handleEncryptFile = () => {
    mutateAsync();
  };

  const handleSecretPhraseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSecretPhrase(e.target.value);
  };

  return (
    <>
      <Dialog
        open={openModal}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => setOpenModal(false)}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogActions>
          <DialogUploadCloseButton onClick={() => setOpenModal(false)}><CloseModalIcon/></DialogUploadCloseButton>
        </DialogActions>
        <DialogTitle sx={{fontSize: '56px', p: '0', lineHeight: 1}}>Upload your file</DialogTitle>
        <DialogActions sx={{justifyContent: 'start', mb: '56px'}}>
          <UploadSwitch
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disableRipple
            icon={<SwitchIcon/>}
            checkedIcon={<SwitchUncheckedIcon/>}
          />
          <UploadColorText active={checked}>Encrypted</UploadColorText>
        </DialogActions>
        {file ? (
          <DialogContentSelected>
            <FileIconBox><FileIcon/></FileIconBox>
            <Box>
              <DialogContentSelectedTitle>{compactFileName(file.name)}</DialogContentSelectedTitle>
              <DialogContentSelectedSubTitle>{formatFileSize(file.size)}</DialogContentSelectedSubTitle>
            </Box>
            <DialogContentSelectedBin onClick={() => setFile(null)}><BinIcon/></DialogContentSelectedBin>
          </DialogContentSelected>
        ) : (
          <DialogContentActive {...getRootProps()}>
            <input {...getInputProps()} />
            <UploadText>
              {isDragActive ? "Drop it here" : "Click here \n or drag & drop"}
            </UploadText>
            {!isDragActive && <UiAddButton aboutFunction={open}/>}
          </DialogContentActive>
        )}
        <DialogContent>
          {checked && (
            <Formik
              enableReinitialize={true}
              validationSchema={UploadModalSchema}
              onSubmit={() => {}}
              initialValues={{ secret_phrase: secretPhrase }}
            >
              {(formik) => {
                const {errors, touched, values} = formik;
                return (
                  <Box mb="32px">
                    <Typography mb="12px">Secret phrase</Typography>
                    <Field
                      mb="32px"
                      as={UiGradientInput}
                      name="secret_phrase"
                      placeholder={'Anything...'}
                      value={values.secret_phrase}
                      onChange={handleSecretPhraseChange}
                      error={(touched.secret_phrase && Boolean(errors.secret_phrase)) || !!errorMessage}
                      helperText={(touched.secret_phrase && errors.secret_phrase) || errorMessage}
                    />
                  </Box>
                )
              }}
            </Formik>
          )}
          <DialogContentText>File will be encrypted with AES-GCM encryption method.</DialogContentText>
          <DialogContentButton
            variant={'contained'}
            onClick={handleEncryptFile}
            disabled={file === null || (checked && secretPhrase === '')}>
            {checked ? 'Upload encrypted file' : 'Upload file'}
          </DialogContentButton>
        </DialogContent>
      </Dialog>
    </>
  )
})
